/**
 * Type union definition of available label sizes.
 * This sizes are human readable format of the character limits.
 * These values don't represent the "size labels" of the products.
 */
export type Size =
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'select';

type SizesWithoutSelect = Exclude<Size, 'select'>

type SizeCharMapping = 'sm' | 'md' | 'lg' | 'max';

type SizeDimension = {
    DimensionId: string;
    DisplayName: string;
};

/**
 * Values for each label size that determines when the label size
 * should go up one size.
 */
export const thresholds: Record<SizesWithoutSelect, number> = {
    'sm': 3,
    'md': 2.25,
    'lg': 2,
    'xl': 1.5,
};

/**
 * Actual character limits for each label size
 */
export const charLimit: Record<SizeCharMapping, number> = {
    'sm': 2,
    'md': 5,
    'lg': 8,
    'max': 20,
};

/**
 * How many items can fit into one row
 */
export const maxItems: Record<SizesWithoutSelect, number> = {
    'sm': 12,
    'md': 10,
    'lg': 8,
    'xl': 4,
};

export default class SizeHelpers {
    // public static sortLabels = (labels: SizeDimension[]): SizeDimension[] => {
    //     const sorted: SizeDimension[] = [].concat(labels);
    //     sorted.sort((a, b) => a.DisplayName.length - b.DisplayName.length);
    //     return sorted;
    // }

    public static determineSize = (labels: SizeDimension[]): Size => {
        const max = SizeHelpers.maxLength(labels);
        const mean = SizeHelpers.meanLength(labels);
        const diff = max / mean;

        // If any label is super-long, don't display as tiles
        // Use select (dropdown) variation
        if (max >= charLimit['max']) {
            return 'select';
        }

        if (max <= charLimit['sm']) {
            return 'sm';
        }

        if (mean <= charLimit['md'] && max <= charLimit['md']) {
            return 'md';
        }

        if (mean <= charLimit['lg'] && max <= charLimit['lg']) {
            return 'lg';
        }

        if (mean <= charLimit['max'] && max < charLimit['max']) {
            return 'xl';
        }

        // Select a size
        const size = SizeHelpers.compareMeanWithCharLimits(mean);
        const bumpedSize = SizeHelpers.bumpIfExceedsThreshold(size, diff);

        return bumpedSize;
    }

    private static maxLength = (labels: SizeDimension[]): number => {
        const lengths = labels.map((label) => label.DisplayName.length);
        let max: number = -1;

        for (let length of lengths) {
            max = length > max ? length : max;
        }

        return max;
    }

    private static meanLength = (labels: SizeDimension[]): number => {
        if (labels.length === 0) {
            return 0;
        }

        const total = labels.reduce((acc, curr) => acc + curr.DisplayName.length, 0);
        const mean = total / labels.length;
        return mean;
    }

    private static compareMeanWithCharLimits = (mean: number): Size => {
        let size: Size = 'xl';

        if (mean <= charLimit['lg']) {
            size = 'lg';
        }

        if (mean <= charLimit['md']) {
            size = 'md';
        }

        if (mean <= charLimit['sm']) {
            size = 'sm';
        }

        return size;
    }

    private static bumpIfExceedsThreshold = (initial: Size, diff: number): Size => {
        let size = initial;

        // Bump up if the mean/max diff exceeds a threshold
        if (size === 'sm' && diff > thresholds['sm']) {
            size = 'md';
        }

        if (size === 'md' && diff > thresholds['md']) {
            size = 'lg';
        }

        if (size === 'lg' && diff > thresholds['lg']) {
            size = 'xl';
        }

        if (size === 'xl' && diff > thresholds['xl']) {
            size = 'select';
        }

        return size;
    }
}